import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import DashboardLayout from '@/components/feature/DashboardLayout';
import PageHeader from '@/components/feature/PageHeader';
import Button from '@/components/base/Button';
import Switch from '@/components/base/Switch';
import Toast, { type ToastType } from '@/components/base/Toast';
import { Pill, type PillTone } from '@/components/base/Badges';
import { driverTasks, driverWeeklyTasks } from '@/mocks/drivers';
import { useAppData, type Driver } from '@/store/AppDataContext';

interface ToastState { type: ToastType; message: string }

const statusToneMap: Record<Driver['status'], PillTone> = {
  available: 'success',
  busy: 'warning',
  offline: 'neutral',
  disabled: 'danger',
};

export default function DriverProfilePage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { drivers, toggleDriverStatus, resetDriverPassword } = useAppData();

  const [toast, setToast] = useState<ToastState | null>(null);
  const [taskFilter, setTaskFilter] = useState<'الكل' | 'استلام' | 'تسليم' | 'مهام اليوم'>('الكل');
  const [sortOrder, setSortOrder] = useState<'newest' | 'oldest'>('newest');

  const driver = drivers.find((d) => d.id === id);

  if (!driver) {
    return (
      <DashboardLayout title="بروفايل المندوب">
        <div className="mx-auto w-full max-w-[640px]">
          <PageHeader title="بروفايل المندوب" backTo="/drivers" />
          <div className="rounded-card border border-background-200 bg-white p-8 text-center shadow-card">
            <i className="ri-user-unfollow-line text-4xl text-foreground-300" aria-hidden="true"></i>
            <p className="mt-3 text-[14px] font-medium text-foreground-700">لم يتم العثور على هذا المندوب</p>
            <p className="mt-1 text-[12px] text-foreground-400">ربما تم حذفه أو الرابط غير صحيح.</p>
            <div className="mt-5 flex justify-center">
              <Button variant="cta" size="md" icon="ri-arrow-right-line" onClick={() => navigate('/drivers')}>
                رجوع لقائمة المندوبين
              </Button>
            </div>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  const isDisabled = driver.status === 'disabled';

  const handleToggleStatus = () => {
    toggleDriverStatus(driver.id);
    setToast({
      type: 'info',
      message: isDisabled ? `تم تفعيل ${driver.name}` : `تم تعطيل ${driver.name}`,
    });
  };

  const handleResetPassword = () => {
    resetDriverPassword(driver.id);
    setToast({ type: 'success', message: `تم إرسال رابط إعادة تعيين كلمة المرور إلى ${driver.name}` });
  };

  const completedTasks = driverTasks.length;
  const maxWeeklyTasks = Math.max(...driverWeeklyTasks.map((d) => d.tasks));
  const weeklyTotal = driverWeeklyTasks.reduce((sum, d) => sum + d.tasks, 0);
  const now = new Date();
  const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;

  const filteredTasks = [...driverTasks]
    .filter((t) => {
      if (taskFilter === 'استلام' || taskFilter === 'تسليم') return t.type === taskFilter;
      if (taskFilter === 'مهام اليوم') return t.date === todayStr;
      return true;
    })
    .sort((a, b) => (sortOrder === 'newest' ? b.date.localeCompare(a.date) : a.date.localeCompare(b.date)));

  const emptyMessage =
    taskFilter === 'مهام اليوم'
      ? 'لا توجد مهام اليوم'
      : taskFilter === 'الكل'
        ? 'لا توجد مهام'
        : `لا توجد مهام من نوع «${taskFilter}»`;

  return (
    <DashboardLayout title={`بروفايل: ${driver.name}`}>
      {toast && <Toast type={toast.type} message={toast.message} onClose={() => setToast(null)} />}

      <div className="space-y-6">
        <PageHeader
          title="بروفايل المندوب"
          subtitle="كل تفاصيل المندوب وسجل مهامه في مكان واحد."
          backTo="/drivers"
          actions={
            <>
              <Button
                variant="outline"
                size="md"
                icon="ri-pencil-line"
                onClick={() => navigate(`/drivers/${driver.id}/edit`)}
              >
                تعديل البيانات
              </Button>
              <Button
                variant="cta"
                size="md"
                icon="ri-lock-password-line"
                onClick={handleResetPassword}
              >
                إعادة تعيين كلمة المرور
              </Button>
            </>
          }
        />

        <div className="rounded-card border border-background-200 bg-white p-5 shadow-card">
          <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex items-center gap-4">
              <div className="h-20 w-20 shrink-0 overflow-hidden rounded-full border border-background-200">
                <img src={driver.photo} alt={driver.name} className="h-full w-full object-cover object-top" />
              </div>
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <h3 className="font-bold text-[18px] text-foreground-950">{driver.name}</h3>
                  <Pill tone={statusToneMap[driver.status]}>{driver.statusLabel}</Pill>
                </div>
                <p className="mt-1 flex items-center gap-1.5 text-[13px] text-foreground-500">
                  <i className="ri-e-bike-2-line text-base" aria-hidden="true"></i>
                  {driver.vehicle}
                </p>
                <p className="mt-0.5 flex items-center gap-1.5 text-[13px] text-foreground-500" dir="ltr">
                  <i className="ri-phone-line text-base" aria-hidden="true"></i>
                  {driver.phone}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3 rounded-card border border-background-200 bg-background-50 px-4 py-3">
              <span className="text-[13px] font-medium text-foreground-700">
                {isDisabled ? 'الحساب معطّل' : 'الحساب مفعّل'}
              </span>
              <Switch checked={!isDisabled} onChange={handleToggleStatus} />
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <div className="rounded-card border border-background-200 bg-white p-4 shadow-card">
            <div className="flex items-center gap-2 text-[12px] text-foreground-500">
              <i className="ri-star-fill text-accent-500" aria-hidden="true"></i>
              التقييم
            </div>
            <p className="mt-1.5 font-bold text-[24px] leading-tight text-foreground-950">{driver.rating}</p>
            <p className="text-[11px] text-foreground-400">من 5.0</p>
          </div>

          <div className="rounded-card border border-background-200 bg-white p-4 shadow-card">
            <div className="flex items-center gap-2 text-[12px] text-foreground-500">
              <i className="ri-checkbox-multiple-line text-secondary-600" aria-hidden="true"></i>
              مهام اليوم
            </div>
            <p className="mt-1.5 font-bold text-[24px] leading-tight text-foreground-950">{driver.tasksToday}</p>
            <p className="text-[11px] text-foreground-400">مهمة</p>
          </div>

          <div className="rounded-card border border-background-200 bg-white p-4 shadow-card">
            <div className="flex items-center gap-2 text-[12px] text-foreground-500">
              <i className="ri-route-line text-info-600" aria-hidden="true"></i>
              المسافة الحالية
            </div>
            <p className="mt-1.5 font-bold text-[24px] leading-tight text-foreground-950">{driver.distance}</p>
            <p className="text-[11px] text-foreground-400">عن المغسلة</p>
          </div>

          <div className="rounded-card border border-background-200 bg-white p-4 shadow-card">
            <div className="flex items-center gap-2 text-[12px] text-foreground-500">
              <i className="ri-history-line text-accent-600" aria-hidden="true"></i>
              إجمالي المهام المسجّلة
            </div>
            <p className="mt-1.5 font-bold text-[24px] leading-tight text-foreground-950">{completedTasks}</p>
            <p className="text-[11px] text-foreground-400">آخر 5 مهام معروضة</p>
          </div>
        </div>

        <div className="rounded-card border border-background-200 bg-white p-5 shadow-card">
          <div className="flex items-center justify-between gap-3">
            <div>
              <h3 className="font-semibold text-[15px] text-foreground-950">المهام على مدار الأسبوع</h3>
              <p className="mt-0.5 text-[12px] text-foreground-400">إجمالي {weeklyTotal} مهمة هذا الأسبوع</p>
            </div>
            <span className="flex items-center gap-1.5 text-[12px] text-foreground-500">
              <i className="ri-bar-chart-2-line text-secondary-600" aria-hidden="true"></i>
              آخر 7 أيام
            </span>
          </div>

          <div className="mt-6 flex h-44 items-end justify-between gap-3 px-1">
            {driverWeeklyTasks.map((item) => {
              const height = Math.round((item.tasks / maxWeeklyTasks) * 100);
              const isPeak = item.tasks === maxWeeklyTasks;
              return (
                <div key={item.day} className="flex flex-1 flex-col items-center gap-2">
                  <span className="text-[12px] font-semibold text-foreground-700">{item.tasks}</span>
                  <div className="flex h-28 w-full max-w-[40px] items-end overflow-hidden rounded-md bg-background-100">
                    <div
                      className={[
                        'w-full rounded-md transition-all',
                        isPeak ? 'bg-primary-500' : 'bg-secondary-400',
                      ].join(' ')}
                      style={{ height: `${height}%` }}
                    />
                  </div>
                  <span className="whitespace-nowrap text-[12px] text-foreground-500">{item.day}</span>
                </div>
              );
            })}
          </div>
        </div>

        <div className="overflow-hidden rounded-card border border-background-200 bg-white shadow-card">
          <div className="flex flex-col gap-3 border-b border-background-100 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
            <h3 className="font-semibold text-[15px] text-foreground-950">سجل المهام الأخيرة</h3>
            <div className="flex flex-wrap items-center gap-3">
              <span className="text-[12px] text-foreground-400">{filteredTasks.length} مهمة</span>
              <button
                type="button"
                onClick={() => setSortOrder(sortOrder === 'newest' ? 'oldest' : 'newest')}
                className="flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-background-200 px-2.5 py-1.5 text-[12px] font-medium text-foreground-600 hover:bg-background-50"
              >
                <i className={sortOrder === 'newest' ? 'ri-sort-desc' : 'ri-sort-asc'} aria-hidden="true"></i>
                {sortOrder === 'newest' ? 'الأحدث أولاً' : 'الأقدم أولاً'}
              </button>
              <div className="flex items-center gap-1 rounded-full bg-background-100 px-1 py-1">
                {(['الكل', 'استلام', 'تسليم', 'مهام اليوم'] as const).map((filter) => (
                  <button
                    key={filter}
                    type="button"
                    onClick={() => setTaskFilter(filter)}
                    className={[
                      'whitespace-nowrap cursor-pointer rounded-full px-3 py-1.5 text-[12px] font-medium transition-colors',
                      taskFilter === filter
                        ? 'bg-white text-foreground-950'
                        : 'text-foreground-500 hover:text-foreground-800',
                    ].join(' ')}
                  >
                    {filter}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[640px] border-collapse text-right">
              <thead>
                <tr className="bg-background-50">
                  {['نوع المهمة', 'رقم الطلب', 'التاريخ', 'رسوم التوصيل'].map((header) => (
                    <th
                      key={header}
                      className="whitespace-nowrap px-4 py-3 font-semibold text-[12px] text-foreground-500"
                    >
                      {header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredTasks.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="px-4 py-10 text-center text-[13px] text-foreground-400">
                      {emptyMessage}
                    </td>
                  </tr>
                ) : (
                  filteredTasks.map((task, index) => (
                    <tr key={task.id} className={index > 0 ? 'border-t border-background-100' : ''}>
                      <td className="px-4 py-3">
                        <span
                          className={[
                            'text-[13px] font-medium',
                            task.type === 'استلام' ? 'text-secondary-600' : 'text-accent-600',
                          ].join(' ')}
                        >
                          {task.type}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-[13px] font-medium text-foreground-900">{task.orderNo}</td>
                      <td className="px-4 py-3 text-[13px] text-foreground-500">
                        {task.date} • {task.time}
                      </td>
                      <td className="px-4 py-3 text-[13px] font-semibold text-success-700">{task.fee}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}